import API_BASE, { protectedApiFetch } from "../api";

export const POST_TRANSLATION_LANGUAGES = Object.freeze([
  "en",
  "bg",
  "el",
  "es",
  "fr",
  "pt",
  "pl",
  "de",
  "tr",
]);

const LANGUAGE_SET = new Set(POST_TRANSLATION_LANGUAGES);
const SOURCE_LANGUAGE_PATTERN =
  /^(?:[a-z]{2,3}(?:-[a-z0-9]{2,8})*|mixed|unknown)$/;
const FAILED_CAPABILITY = Object.freeze({
  enabled: false,
  supportedLanguages: Object.freeze([]),
});

let capabilityPromise = null;
let capabilityResult = null;
let capabilityGeneration = 0;

export class PostTranslationApiError extends Error {
  constructor(code, { status = 0 } = {}) {
    super("Post translation request failed.");
    this.name = "PostTranslationApiError";
    this.code = code || "post_translation_unavailable";
    this.status = status;
  }
}

function normalizeCapability(payload) {
  if (
    !payload ||
    typeof payload.enabled !== "boolean" ||
    !Array.isArray(payload.supportedLanguages)
  ) {
    throw new PostTranslationApiError("post_translation_response_malformed");
  }

  const supportedLanguages = payload.supportedLanguages.filter(
    (language, index, languages) =>
      typeof language === "string" &&
      LANGUAGE_SET.has(language) &&
      languages.indexOf(language) === index
  );
  if (
    supportedLanguages.length !== payload.supportedLanguages.length ||
    (payload.enabled && supportedLanguages.length === 0)
  ) {
    throw new PostTranslationApiError("post_translation_response_malformed");
  }

  return Object.freeze({
    enabled: payload.enabled,
    supportedLanguages: Object.freeze(supportedLanguages),
  });
}

function normalizeTranslation(payload, { expectedPostId, expectedTargetLanguage }) {
  let sourceLanguage = null;
  if (payload?.sourceLanguage !== null) {
    if (typeof payload?.sourceLanguage !== "string") {
      throw new PostTranslationApiError("post_translation_response_malformed");
    }
    sourceLanguage = payload.sourceLanguage.trim().toLowerCase();
    if (!SOURCE_LANGUAGE_PATTERN.test(sourceLanguage)) {
      throw new PostTranslationApiError("post_translation_response_malformed");
    }
  }
  if (
    !payload ||
    typeof payload.postId !== "string" ||
    !payload.postId.trim() ||
    payload.postId !== expectedPostId ||
    typeof payload.targetLanguage !== "string" ||
    !LANGUAGE_SET.has(payload.targetLanguage) ||
    payload.targetLanguage !== expectedTargetLanguage ||
    typeof payload.translatedText !== "string" ||
    !payload.translatedText.trim() ||
    payload.translatedText.includes("\u0000") ||
    payload.translatedText.length > 8_000 ||
    typeof payload.cached !== "boolean" ||
    typeof payload.sameLanguage !== "boolean"
  ) {
    throw new PostTranslationApiError("post_translation_response_malformed");
  }

  return Object.freeze({
    postId: payload.postId,
    sourceLanguage,
    targetLanguage: payload.targetLanguage,
    translatedText: payload.translatedText,
    cached: payload.cached,
    sameLanguage: payload.sameLanguage,
  });
}

async function readJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function responseError(response) {
  const payload = await readJson(response);
  const code =
    typeof payload?.detail === "string" && payload.detail.trim()
      ? payload.detail.trim()
      : "post_translation_unavailable";
  return new PostTranslationApiError(code, { status: response?.status || 0 });
}

export function getPostTranslationStatus() {
  if (capabilityResult) {
    return Promise.resolve(capabilityResult);
  }
  if (capabilityPromise) {
    return capabilityPromise;
  }

  const requestGeneration = capabilityGeneration;
  capabilityPromise = (async () => {
    let result;
    try {
      const response = await protectedApiFetch(
        `${API_BASE}/posts/translation/status`,
        {
          method: "GET",
          cache: "no-store",
        }
      );
      if (!response.ok) {
        throw await responseError(response);
      }
      result = normalizeCapability(await readJson(response));
    } catch {
      result = FAILED_CAPABILITY;
    }
    if (requestGeneration === capabilityGeneration) {
      capabilityResult = result;
    }
    return result;
  })();

  return capabilityPromise;
}

export async function translatePostCaption(
  postId,
  targetLanguage,
  { signal } = {}
) {
  const normalizedPostId = String(postId || "").trim();
  if (!normalizedPostId || !LANGUAGE_SET.has(targetLanguage)) {
    throw new PostTranslationApiError("post_translation_request_invalid");
  }

  const response = await protectedApiFetch(
    `${API_BASE}/posts/${encodeURIComponent(normalizedPostId)}/translate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ targetLanguage }),
      signal,
    }
  );
  if (!response.ok) {
    throw await responseError(response);
  }
  return normalizeTranslation(await readJson(response), {
    expectedPostId: normalizedPostId,
    expectedTargetLanguage: targetLanguage,
  });
}

export function resetPostTranslationCapabilityForTests() {
  capabilityGeneration += 1;
  capabilityPromise = null;
  capabilityResult = null;
}
